import { useCallback, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { TablePagination } from "components/paginations/TablePagination"
import { SearchInput } from "components/search/SearchInput"
import { Column, DataTable } from "components/tables/DataTable"

import { gameService } from "services/game/game.service"
import { CategorySeoEntry, GameInterface } from "services/game/type"

import { useBrandSite } from "../BrandSiteContext"
import { AsyncList, IndexFlag, InfoBox, LocaleChips, PathText, primaryButtonClass } from "../CmsUi"
import { GameSeoModal } from "../GameSeoModal"

const PAGE_SIZE = 25
const FALLBACK_LOCALE = "en"

/** Locales this game has copy of its own for. */
const authoredLocales = (game: GameInterface): string[] =>
  Object.entries(game.seo ?? {})
    .filter(([key, value]) => {
      if (key === "noindex") return false
      const entry = value as CategorySeoEntry
      return Boolean(entry?.title || entry?.description || entry?.h1 || entry?.footer?.html?.trim())
    })
    .map(([key]) => key)

/**
 * Every game the catalogue routes, with the SEO it has. Paged and searchable:
 * a brand serves tens of thousands.
 */
export function GameSeoTab() {
  const { t } = useTranslation("translation", { useSuspense: false })
  const site = useBrandSite()

  const [games, setGames] = useState<GameInterface[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [editing, setEditing] = useState<GameInterface | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const data = await gameService.getAllGames({
      page,
      pageSize: PAGE_SIZE,
      search,
      includeDisabled: true
    })
    setLoading(false)
    if (!data) return
    setGames((data.games ?? []) as GameInterface[])
    setTotalPages(data.total_pages ?? 1)
  }, [page, search])

  useEffect(() => {
    load()
  }, [load])

  const columns: Column<GameInterface>[] = [
    {
      header: t("cmsSeo.col.game", { defaultValue: "Game" }),
      render: (game) => <span className="text-sm font-medium">{game.title}</span>
    },
    {
      header: t("cmsSeo.col.path", { defaultValue: "Path" }),
      render: (game) => {
        const path = `/game/${game.identifier}`
        if (!site.origin) return <PathText>{path}</PathText>
        return (
          <a
            href={site.url(site.defaultLocale, path)}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            <PathText className="underline decoration-dotted underline-offset-2">{path}</PathText>
          </a>
        )
      }
    },
    {
      header: t("cmsSeo.col.title", { defaultValue: "Title" }),
      wrap: true,
      render: (game) => {
        const entry = game.seo?.[FALLBACK_LOCALE] as CategorySeoEntry | undefined
        if (entry?.title) return <span className="text-sm">{entry.title}</span>
        return (
          <span className="text-sm opacity-50">
            {t("cmsSeo.gamesDefaultTitle", { defaultValue: "{{name}} (automatic)", name: game.title })}
          </span>
        )
      }
    },
    {
      header: t("cmsSeo.col.index", { defaultValue: "Index" }),
      hideBelow: "md",
      // A flag reports a decision; without copy there is none to report.
      render: (game) =>
        authoredLocales(game).length > 0 ? (
          <IndexFlag indexed={game.seo?.noindex !== true} />
        ) : (
          <span className="text-xs opacity-50">{t("cmsSeo.gamesNoSeo", { defaultValue: "Default" })}</span>
        )
    },
    {
      header: t("cmsSeo.col.locales", { defaultValue: "Locales" }),
      hideBelow: "lg",
      render: (game) => <LocaleChips locales={authoredLocales(game)} missing={[]} />
    },
    {
      header: t("common.actions", { defaultValue: "Actions" }),
      align: "right",
      render: (game) => (
        <button
          type="button"
          className={primaryButtonClass}
          onClick={(event) => {
            event.stopPropagation()
            setEditing(game)
          }}
        >
          {authoredLocales(game).length > 0
            ? (t("common.edit", { defaultValue: "Edit" }) as string)
            : (t("cmsSeo.addSeo", { defaultValue: "Add SEO" }) as string)}
        </button>
      )
    }
  ]

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-5">
      <div className="w-full sm:w-72">
        <SearchInput
          placeholder={t("cmsSeo.searchGames", { defaultValue: "Search by game title…" }) as string}
          onChange={(value: string) => {
            setSearch(value)
            setPage(1)
          }}
        />
      </div>

      <InfoBox>
        {t("cmsSeo.gamesInfo", {
          defaultValue:
            "Every game the catalogue routes. Each page already has a working title and H1 taken from the game's own name — add copy here only where you want something different."
        })}
        <div className="mt-2">
          {t("cmsSeo.gamesSitemapInfo", {
            defaultValue:
              "Only games with copy of their own are submitted to search engines. A catalogue this size would otherwise fill the index with near-identical pages."
          })}
        </div>
        <div className="mt-2">
          {t("cmsSeo.gamesDescriptionInfo", {
            defaultValue:
              "The Description in Game Management is separate copy shown in the same block on the page. It is the same in every language; what you set here is per language."
          })}
        </div>
      </InfoBox>

      <AsyncList
        loading={loading}
        isEmpty={games.length === 0}
        emptyText={t("cmsSeo.noGames", { defaultValue: "No games match this search." }) as string}
      >
        <DataTable
          rows={games}
          rowKey={(game) => game.identifier}
          columns={columns}
          onRowClick={(game) => setEditing(game)}
        />
      </AsyncList>

      <TablePagination page={page} totalPages={totalPages} onJump={setPage} />

      <GameSeoModal game={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  )
}
